// MUNYA OS · Button · sistema componentes ui/ v1 (Workstream D3 · § 18.2)
//
// 7 variantes según brandbook: primary CTA · secondary · destructive · ghost · link · icon · loading.
// 4 tamaños: sm · md (default) · lg · icon.
// Accesibilidad: focus-visible outline (2px verde + offset 2), aria-disabled, aria-busy.

import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react';

export type ButtonVariant = 'primary' | 'secondary' | 'destructive' | 'ghost' | 'link' | 'icon';
export type ButtonSize = 'sm' | 'md' | 'lg';

const VARIANT: Record<ButtonVariant, string> = {
  primary:
    'bg-[var(--color-munya-olive)] text-white border border-[var(--color-munya-olive)] hover:bg-[var(--color-munya-olive-hover)] hover:shadow-[0_8px_24px_rgba(126,140,105,0.3)] hover:-translate-y-px',
  secondary:
    'bg-transparent text-[var(--color-munya-olive)] border border-[var(--color-munya-olive)]/60 hover:bg-[var(--color-munya-olive)]/5',
  destructive:
    'bg-[var(--color-munya-destructive)] text-white border border-[var(--color-munya-destructive)] hover:bg-[var(--color-munya-destructive)]/90',
  ghost:
    'bg-transparent text-[var(--color-munya-olive)] border border-transparent hover:bg-[var(--color-munya-olive)]/8',
  link: 'bg-transparent text-[var(--color-munya-olive)] border-transparent p-0 underline underline-offset-2 hover:opacity-80',
  icon: 'bg-transparent text-[var(--color-munya-coffee)] border border-transparent hover:bg-[var(--color-munya-olive)]/8 rounded',
};

const SIZE: Record<ButtonSize, string> = {
  sm: 'h-8 px-3 text-xs gap-1.5',
  md: 'h-10 px-4 text-sm gap-2',
  lg: 'h-12 px-5 text-base gap-2',
};

const ICON_SIZE: Record<ButtonSize, string> = {
  sm: 'h-8 w-8 p-0',
  md: 'h-10 w-10 p-0',
  lg: 'h-12 w-12 p-0',
};

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  loading?: boolean;
  leadingIcon?: ReactNode;
  trailingIcon?: ReactNode;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    variant = 'primary',
    size = 'md',
    loading,
    leadingIcon,
    trailingIcon,
    disabled,
    className = '',
    children,
    ...rest
  },
  ref,
) {
  const isIconOnly = variant === 'icon';
  const isDisabled = disabled || loading;
  const sizeCls = isIconOnly ? ICON_SIZE[size] : SIZE[size];

  return (
    <button
      ref={ref}
      type={rest.type ?? 'button'}
      disabled={isDisabled}
      aria-disabled={isDisabled || undefined}
      aria-busy={loading || undefined}
      className={[
        'inline-flex items-center justify-center rounded font-medium leading-none',
        'transition-all duration-200 ease-out',
        'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-munya-olive)]',
        'disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:translate-y-0 disabled:hover:shadow-none',
        VARIANT[variant],
        sizeCls,
        className,
      ].join(' ')}
      {...rest}
    >
      {loading ? (
        <span className="inline-block h-3.5 w-3.5 motion-safe:animate-spin rounded-full border-2 border-current border-t-transparent" />
      ) : (
        <>
          {leadingIcon}
          {!isIconOnly && children}
          {trailingIcon}
          {isIconOnly && children}
        </>
      )}
    </button>
  );
});
